import { MediaItem } from "@/utils/mediaTypes";

const EMBY_AUTH_HEADER =
  'MediaBrowser Client="movie-web", Device="Browser", DeviceId="movie-web-browser", Version="1.0.0"';

export interface JellyfinAuthResult {
  token: string;
  userId: string;
  username: string;
}

export interface JellyfinItem {
  Id: string;
  Name: string;
  Type: "Movie" | "Series";
  ProductionYear?: number;
  ImageTags?: { Primary?: string };
  BackdropImageTags?: string[];
}

export interface JellyfinItemDetails {
  Id: string;
  Name: string;
  Type: "Movie" | "Series";
  ProductionYear?: number;
  ImageTags?: { Primary?: string };
  Overview?: string;
  ProviderIds?: { Tmdb?: string; Imdb?: string };
}

export interface JellyfinSeason {
  Id: string;
  Name: string;
  IndexNumber?: number;
}

export interface JellyfinEpisode {
  Id: string;
  Name: string;
  IndexNumber?: number;
  ParentIndexNumber?: number;
  PremiereDate?: string;
}

export function jellyfinImageUrl(
  serverUrl: string,
  itemId: string,
  tag: string,
): string {
  return `${serverUrl}/Items/${itemId}/Images/Primary?maxHeight=300&maxWidth=200&tag=${encodeURIComponent(tag)}&quality=90`;
}

export interface JellyfinBrandingConfig {
  LoginDisclaimer?: string;
  CustomCss?: string;
}

export async function getJellyfinBranding(
  serverUrl: string,
): Promise<JellyfinBrandingConfig> {
  try {
    const res = await fetch(`${serverUrl}/Branding/Configuration`);
    if (!res.ok) return {};
    return res.json();
  } catch (err) {
    console.warn(
      "Failed to fetch Jellyfin branding configuration (non-critical, continuing with defaults):",
      err,
    );
    return {};
  }
}

export async function authenticateJellyfin(
  serverUrl: string,
  username: string,
  password: string,
): Promise<JellyfinAuthResult> {
  const res = await fetch(`${serverUrl}/Users/AuthenticateByName`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Emby-Authorization": EMBY_AUTH_HEADER,
    },
    body: JSON.stringify({ Username: username, Pw: password }),
  });

  if (!res.ok) {
    if (res.status === 401) throw new Error("jellyfin.auth.invalidCredentials");
    throw new Error("jellyfin.auth.connectionFailed");
  }

  const data = await res.json();
  return {
    token: data.AccessToken,
    userId: data.User.Id,
    username: data.User.Name,
  };
}

export async function getJellyfinItemDetails(
  serverUrl: string,
  token: string,
  userId: string,
  itemId: string,
): Promise<JellyfinItemDetails> {
  const res = await fetch(
    `${serverUrl}/Users/${userId}/Items/${itemId}?Fields=ProviderIds`,
    { headers: { Authorization: `MediaBrowser Token="${token}"` } },
  );
  if (!res.ok)
    throw new Error(
      `Failed to fetch Jellyfin item details: ${res.status} ${res.statusText}`,
    );
  return res.json();
}

export async function getJellyfinSeasons(
  serverUrl: string,
  token: string,
  userId: string,
  seriesId: string,
): Promise<JellyfinSeason[]> {
  const res = await fetch(
    `${serverUrl}/Shows/${seriesId}/Seasons?UserId=${userId}`,
    { headers: { Authorization: `MediaBrowser Token="${token}"` } },
  );
  if (!res.ok)
    throw new Error(
      `Failed to fetch Jellyfin seasons: ${res.status} ${res.statusText}`,
    );
  const data: { Items: JellyfinSeason[] } = await res.json();
  return data.Items;
}

export async function getJellyfinEpisodes(
  serverUrl: string,
  token: string,
  userId: string,
  seriesId: string,
  seasonId: string,
): Promise<JellyfinEpisode[]> {
  const res = await fetch(
    `${serverUrl}/Shows/${seriesId}/Episodes?SeasonId=${seasonId}&UserId=${userId}`,
    { headers: { Authorization: `MediaBrowser Token="${token}"` } },
  );
  if (!res.ok)
    throw new Error(
      `Failed to fetch Jellyfin episodes: ${res.status} ${res.statusText}`,
    );
  const data: { Items: JellyfinEpisode[] } = await res.json();
  return data.Items;
}

export interface JellyfinResumeItem {
  Id: string;
  Name: string;
  Type: "Movie" | "Episode";
  SeriesName?: string;
  SeriesId?: string;
  SeasonId?: string;
  IndexNumber?: number;
  ParentIndexNumber?: number;
  ProductionYear?: number;
  ImageTags?: { Primary?: string };
  SeriesImageTags?: { Primary?: string };
  UserData?: {
    PlayedPercentage?: number;
    PlaybackPositionTicks?: number;
  };
}

export async function getJellyfinResumeItems(
  serverUrl: string,
  token: string,
  userId: string,
): Promise<JellyfinResumeItem[]> {
  const params = new URLSearchParams({
    Limit: "20",
    Fields: "UserData,SeriesImageTags",
    EnableImageTypes: "Primary",
    ImageTypeLimit: "1",
  });
  const res = await fetch(
    `${serverUrl}/Users/${userId}/Items/Resume?${params.toString()}`,
    { headers: { Authorization: `MediaBrowser Token="${token}"` } },
  );
  if (!res.ok)
    throw new Error(
      `Failed to fetch resume items: ${res.status} ${res.statusText}`,
    );
  const data: { Items: JellyfinResumeItem[] } = await res.json();
  return data.Items;
}

export interface JellyfinMediaStream {
  Index: number;
  Type: "Video" | "Audio" | "Subtitle" | string;
  Language?: string;
  DisplayTitle?: string;
  IsDefault: boolean;
}

export interface JellyfinMediaSource {
  Id: string;
  Name: string;
  MediaStreams?: JellyfinMediaStream[];
}

export async function getJellyfinMediaSources(
  serverUrl: string,
  token: string,
  userId: string,
  itemId: string,
): Promise<JellyfinMediaSource[]> {
  try {
    const res = await fetch(
      `${serverUrl}/Users/${userId}/Items/${itemId}?Fields=MediaSources`,
      { headers: { Authorization: `MediaBrowser Token="${token}"` } },
    );
    if (!res.ok) return [];
    const data: { MediaSources?: JellyfinMediaSource[] } = await res.json();
    return data.MediaSources ?? [];
  } catch {
    return [];
  }
}

export interface JellyfinSubtitleStream {
  Index: number;
  Type: string;
  Language?: string;
  IsExternal: boolean;
  SupportsExternalStream: boolean;
  DeliveryUrl?: string;
}

export async function getJellyfinSubtitleStreams(
  serverUrl: string,
  token: string,
  userId: string,
  itemId: string,
): Promise<JellyfinSubtitleStream[]> {
  const res = await fetch(
    `${serverUrl}/Users/${userId}/Items/${itemId}?Fields=MediaStreams`,
    { headers: { Authorization: `MediaBrowser Token="${token}"` } },
  );
  if (!res.ok) return [];
  const data: { MediaStreams?: JellyfinSubtitleStream[] } = await res.json();
  return (data.MediaStreams ?? []).filter(
    (s) => s.Type === "Subtitle" && s.SupportsExternalStream,
  );
}

export interface JellyfinPlaybackBody {
  ItemId: string;
  MediaSourceId: string;
  PlaySessionId: string;
  PlayMethod: "Transcode" | "DirectStream" | "DirectPlay";
  PositionTicks?: number;
  IsPaused?: boolean;
}

export async function reportJellyfinPlayback(
  serverUrl: string,
  token: string,
  action: "start" | "progress" | "stop",
  body: JellyfinPlaybackBody,
): Promise<void> {
  const endpoint =
    action === "start"
      ? "/Sessions/Playing"
      : action === "stop"
        ? "/Sessions/Playing/Stopped"
        : "/Sessions/Playing/Progress";
  await fetch(`${serverUrl}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `MediaBrowser Token="${token}"`,
    },
    body: JSON.stringify(body),
  });
}

export async function searchJellyfin(
  serverUrl: string,
  token: string,
  userId: string,
  query: string,
): Promise<MediaItem[]> {
  const params = new URLSearchParams({
    SearchTerm: query,
    IncludeItemTypes: "Movie,Series",
    Recursive: "true",
    Fields: "PrimaryImageAspectRatio",
    Limit: "30",
  });

  const res = await fetch(
    `${serverUrl}/Users/${userId}/Items?${params.toString()}`,
    {
      headers: {
        Authorization: `MediaBrowser Token="${token}"`,
      },
    },
  );

  if (!res.ok) throw new Error("jellyfin.search.failed");

  const data: { Items: JellyfinItem[] } = await res.json();
  return data.Items.map((item) => ({
    id: item.Id,
    title: item.Name,
    year: item.ProductionYear,
    type: item.Type === "Series" ? "show" : "movie",
    poster: item.ImageTags?.Primary
      ? jellyfinImageUrl(serverUrl, item.Id, item.ImageTags.Primary)
      : undefined,
  }));
}
